import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import LineExclamationCircleIcon from 'components/Luxkit/Icons/line/LineExclamationCircleIcon'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import LineSightseeingIcon from 'components/Luxkit/Icons/line/LineSightseeingIcon'
import TextLink from 'components/Luxkit/TextLink'
import AlwaysOnCheapestRateOptionMobile from 'components/OfferPage/AlwaysOn/MobileBookingFlow/AlwaysOnCheapestRateOptionMobile'
import AlwaysOnRoomDetailsModal from 'components/OfferPage/AlwaysOn/MobileBookingFlow/Modals/AlwaysOnRoomDetailsModal'
import BedbankRoomCheapestOptionRateMobile from 'components/OfferPage/Bedbank/MobileBookingFlow/BedbankRoomCheapestOptionRateMobile'
import BedbankRoomDetailsModal from 'components/OfferPage/Bedbank/MobileBookingFlow/Modals/BedbankRoomDetailsModal'
import AccommodationRoomTileDetailsVariant from 'components/OfferPage/FlashHotel/FlashHotelMobileVariant/AccommodationRoomTileDetailsVariant'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Plural from 'components/utils/Formatters/Plural'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useLPCUniqueOptions from 'hooks/OfferPage/useLPCUniqueOptions'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useQueryParams from 'hooks/useQueryParams'
import { initialRooms } from 'lib/offer/occupancyUtils'
import { rem } from 'polished'
import React, { MouseEventHandler, useCallback, useContext, useMemo } from 'react'
import { arePricesLoading, getCheckoutDate, isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import AccommodationRoomTile from '../../Accommodation/AccommodationRoomTile'
import AccommodationRoomTileImage from '../../Accommodation/AccommodationRoomTileImage'
import { getCombinedRates, getRateListKey, isRoomRateUnmatched } from '../singlePropertyUtils'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import usePropertyRatesList from 'hooks/usePropertyRatesList'
import { min } from 'lib/array/arrayUtils'

const RateSection = styled.div`
  padding: ${rem(16)} ${rem(20)} ${rem(20)};
`

interface Props {
  offer: App.Offer;
  bedbankOffer?: App.BedbankOffer;
  packageViews: Array<OfferPackageOptionView>;
  bedbankViews: Array<App.BedbankOfferPackageView>;
  checkIn?: string;
  checkOut?: string;
  rooms: Array<App.Occupants>;
  previewMode: boolean;
  isHighlighted?: boolean;
  roomRateId?: string;
  duration?: number;
  pricesLoading?: boolean;
  fetchingBedbankViews?: boolean;
  detailsModalMode: 'room-only' | 'room-and-package';
  availableForPurchase?: boolean;
}

function PropertyRoomCheapestOptionMobile(props: Props) {
  const {
    offer,
    bedbankOffer,
    packageViews,
    bedbankViews,
    checkIn,
    checkOut,
    rooms,
    previewMode,
    isHighlighted,
    roomRateId,
    duration,
    pricesLoading,
    fetchingBedbankViews,
    detailsModalMode,
    availableForPurchase,
  } = props

  const showModal = useContext(ModalContext)
  const offerPageDispatch = useOfferPageDispatch()
  const utmContent = useQueryParams().get('utm_content') || undefined

  // trust me bro, it's a hotel package
  const pkg = packageViews[0].package as App.HotelPackage

  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable) || bedbankViews.some(view => view.available),
  ], [packageViews, bedbankViews])

  const availablePackageViews = useMemo(() => {
    const requiresRates = offer.property?.useDynamicCancellationPolicies && !!checkIn && !pricesLoading
    return packageViews.filter(view => view.capacityValid && view.hotelAvailable && (!requiresRates || view.hasAvailableRates))
  }, [checkIn, offer, packageViews, pricesLoading])

  /**
   * Preview is used only by content people.
   * We don't want to show unavailable rateOptions to the user.
   */
  const options = previewMode ? packageViews : availablePackageViews
  const useRatesEndpoint = useOptimizelyExperiment(OptimizelyFeatureFlags.accomUsePropertyPageRatesEndpoint)
  const availableOptions = useLPCUniqueOptions(
    offer.id,
    options,
    {
      useDynamicCancellationPolicies: offer.property?.useDynamicCancellationPolicies ?? false,
      duration,
      checkIn,
      rooms: rooms ?? initialRooms,
      showAll: useRatesEndpoint,
    },
  )

  const roomRateUnmatched = useMemo(() => isRoomRateUnmatched(roomRateId, { availableOptions, isHighlighted, utmContent }), [availableOptions, isHighlighted, roomRateId, utmContent])

  const stayPayTier = useMemo(() => {
    return packageViews.map(view => {
      return view.hotelAvailable && view.package?.stayPay?.find(tier => tier.stayNights === duration && tier.freeNights > 0)
    }).find(Boolean)
  }, [packageViews, duration])

  const [propertyRates] = usePropertyRatesList(offer.property?.id, {
    checkIn,
    checkOut,
    occupancies: rooms ?? initialRooms,
    dynamicAvailability: offer.property?.useDynamicAvailability,
    disabled: !useRatesEndpoint,
  })

  const displayRates = useMemo(() => {
    const combinedRates = getCombinedRates(
      packageViews,
      bedbankViews,
      availableOptions,
      {
        previewMode,
        showAll: useRatesEndpoint,
        roomRateId,
        isHighlighted,
        utmContent,
      },
    )

    if (useRatesEndpoint && checkIn && checkOut) {
      const rateIds = new Set(propertyRates.map(rate => rate.key))
      return combinedRates.filter(rate => rateIds.has(getRateListKey(rate)))
    }
    return combinedRates
  }, [availableOptions, bedbankViews, checkIn, checkOut, isHighlighted, packageViews, previewMode, propertyRates, roomRateId, useRatesEndpoint, utmContent])

  const cheapestPackageView = useMemo(() => min(displayRates, rate => rate.price), [displayRates])

  const hasMultipleCancellationPolicies = useMemo(() => displayRates.some(rate => rate.refundable !== displayRates[0]?.refundable), [displayRates])

  const showPartialRefundPolicyLabel = useMemo(() => hasMultipleCancellationPolicies && displayRates.some(rate => rate.bedbankRate?.rate.partiallyRefundable), [hasMultipleCancellationPolicies, displayRates])

  const handleOpenCalendar = useCallback<MouseEventHandler<HTMLAnchorElement>>(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, pkg.roomType])

  const openCapacityFilter = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch, pkg.roomType])

  const onShowBedbankRoomDetails = useCallback(() => {
    const packageView = cheapestPackageView?.bedbankRate?.view
    if (packageView == null) {
      return
    }
    if (detailsModalMode === 'room-and-package') {
      // Reset LPC room type for Single Property pages
      offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
      offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: packageView.package.roomId })
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'roomDetails' })
    } else if (pkg.roomType) {
      // Prefer the LPC room type for Single Property pages
      showModal(<AlwaysOnRoomDetailsModal room={pkg.roomType} />, true)
    } else {
      showModal(<BedbankRoomDetailsModal packageView={packageView} />, true)
    }
  }, [cheapestPackageView?.bedbankRate?.view, detailsModalMode, offerPageDispatch, showModal, pkg.roomType])

  return (
    <>
      {checkIn && stayPayTier && <MessageBanner
        kind="promotional-primary"
        icon={<LineInfoCircleIcon />}
        description={<>
          <b>Special offer:</b> You get <Plural singular="night" count={stayPayTier.freeNights} withCount />{' '}
          free when staying <Plural singular="night" count={ stayPayTier.stayNights} withCount /> on select packages.
        </>}
      />}
      <Pane border="neutral-five" borderRadius="S" data-testid="package-option">
        {isHighlighted && <MessageBanner
          kind="promotional-primary"
          description={<b>You were interested in this option</b>}
          icon={!roomRateUnmatched && <LineSightseeingIcon />}
        />}
        <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
        <AccommodationRoomTile roomPackage={pkg}>
          <AccommodationRoomTileImage
            roomPackage={pkg}
          />
          <AccommodationRoomTileDetailsVariant
            roomPackage={pkg}
            urgencyLabels={<>
              <BookingsCountLabel bookings={pkg.roomType.stats.booked} threshold="room" />
              {checkIn && <RoomsRemainingLabel roomCount={packageViews[0].availableRooms ?? 0} threshold="rate" />}
            </>}
            actions={availableForPurchase && roomFitsCapacity && datesAvailable && <CSSBreakpoint min="desktop">
              <TripGuard>
                <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
              </TripGuard>
            </CSSBreakpoint>}
          />
        </AccommodationRoomTile>
        <RateSection data-testid="rate-options">
          {!cheapestPackageView && <>
            {!roomFitsCapacity && (
              <MessageBanner
                kind="informational-primary"
                icon={<LineExclamationCircleIcon />}
                heading="This room cannot accommodate your number of guests"
                description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={openCapacityFilter}>add more rooms</TextLink></>}
              />
            )}
            {!datesAvailable && (
              <MessageBanner
                kind="informational-primary"
                icon={<LineExclamationCircleIcon />}
                heading="This room is sold out on your dates or max number of guests exceeded"
                description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleOpenCalendar}>try different dates</TextLink></>}
              />
            )}
          </>}
          {cheapestPackageView?.lpcRate && <AlwaysOnCheapestRateOptionMobile
            offer={offer}
            packageView={cheapestPackageView.lpcRate}
            detailsModalMode={detailsModalMode}
            hasMultipleCancellationPolicies={hasMultipleCancellationPolicies}
            showPartialRefundPolicyLabel={showPartialRefundPolicyLabel}
          />}
          {cheapestPackageView?.bedbankRate && bedbankOffer && <BedbankRoomCheapestOptionRateMobile
            rate={cheapestPackageView.bedbankRate.rate}
            offer={bedbankOffer}
            packageView={cheapestPackageView.bedbankRate.view}
            hasMultipleCancellationPolicies={hasMultipleCancellationPolicies}
            showPartialRefundPolicyLabel={showPartialRefundPolicyLabel}
            onShowRoomDetails={onShowBedbankRoomDetails}
          />}
          {fetchingBedbankViews && <LoadingIndicator inline />}
        </RateSection>
      </Pane>
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  checkOut: getCheckoutDate(state),
  duration: state.duration,
  previewMode: appState.system.previewMode,
  roomRateId: state.roomOption?.roomRateId,
  pricesLoading: arePricesLoading(appState, state),
  availableForPurchase: isAvailableForPurchase(state, appState),
}), PropertyRoomCheapestOptionMobile)
